import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FormGroup, FormGroupForResolver } from "db/definitions";
import { buildFormGroupForFormGroupList, bulkGetFormGroups } from "db/queries/formGroupQueries";
// @ts-ignore
import { batchGetFormGroupsByIds, batchGetFormGroupsWithFormInstancesAndTemplatesByIds } from "graphql/queries";
import { recordSentry } from "utils/recordSentry";
import { handleBatchCrud } from "./handleBatchCrud";

/**
 * Given an array of formGroup ids and whether or not we're online, retrieves the formGroups and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The formGroup ids we want to fetch
 * @param online Whether or not we're online
 * @returns The array of formGroups retrieved
 */
export async function batchGetFormGroups(ids: string[], online: boolean): Promise<(FormGroupForResolver | null | undefined)[]> {
	if (ids.length > 0) {
		try {
			if (online) {
				const formGroups = await handleBatchCrud(ids, getFormGroups, 100);
				return formGroups;
			} else {
				const formGroups = await bulkGetFormGroups(ids);
				return formGroups;
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FORM GROUPS ${online ? "ONLINE" : "OFFLINE"}` });
		}
	}
	return [];
}

async function getFormGroups(ids: string[]): Promise<FormGroupForResolver[]> {
	interface QueryResponse {
		batchGetFormGroups: FormGroupForResolver[];
	}
	const client = generateClient({ authMode: "userPool" });
	const result = (await client.graphql({
		query: batchGetFormGroupsByIds,
		authMode: "userPool",
		variables: {
			ids,
		},
	})) as GraphQLResult<QueryResponse>;
	return result.data.batchGetFormGroups;
}

/**
 * Given an array of formGroup ids and whether or not we're online, retrieves the formGroups and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param ids The formGroup ids we want to fetch
 * @param online Whether or not we're online
 * @returns The array of formGroups retrieved
 */
export async function batchGetFormGroupsToFormInstance(ids: string[], online: boolean): Promise<FormGroup[]> {
	if (ids.length > 0) {
		try {
			if (online) {
				const formGroups = await handleBatchCrud(ids, getFormGroupsToFormInstance, 100);
				return formGroups;
			} else {
				const retrievedFormGroups = await bulkGetFormGroups(ids);
				const formGroupPromises = retrievedFormGroups.map((fg) => buildFormGroupForFormGroupList(fg));
				const formGroups = await Promise.all(formGroupPromises);
				return formGroups;
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FORM GROUPS ${online ? "ONLINE" : "OFFLINE"}` });
		}
	}
	return [];
}

async function getFormGroupsToFormInstance(ids: string[]): Promise<FormGroup[]> {
	interface QueryResponse {
		batchGetFormGroups: FormGroup[];
	}
	const client = generateClient({ authMode: "userPool" });
	const result = (await client.graphql({
		query: batchGetFormGroupsWithFormInstancesAndTemplatesByIds,
		authMode: "userPool",
		variables: {
			ids,
		},
	})) as GraphQLResult<QueryResponse>;
	return result.data.batchGetFormGroups;
}
